import { type QueryClient } from '@tanstack/react-query';

import { isRecordingInProgress } from '@/hooks/desktop/useChronicleSessionControl';
import { pTimeout } from '@/lib/utils/pTimeout';
import { recorderFmc } from '@/tauri/controller/recording';
import { onlineChunkUploadBridge } from '@/tauri/online-chunk-upload/session';
import { classifyRecorderLiveness } from '@/tauri/recorder-liveness';
import { logger } from '@/utils/logger/logger';

const RECORDING_ACTIVE_PROBE_TIMEOUT_MS = 5000;

/**
 * DSK-696: is a desktop recording active right now, from either side of the
 * IPC boundary?
 *
 * `isRecordingInProgress` (the FE layer — Redux timer, Chronicle session
 * atom, query cache) is checked first as it's synchronous and covers the
 * common desktop-mic/Chronicle/offline paths without an IPC round trip. But
 * Rust owns audio capture independently and can still be recording/uploading
 * a chunk stream while FE state hasn't caught up (e.g. mid-recovery from a
 * prior reload) — so a reload warning gated on FE state alone could be
 * silently bypassed. The Rust probes are the fallback for that gap.
 */
export async function isDesktopRecordingActive(opts: {
  sessionId: string;
  chronicleSessionId: string | null;
  queryClient: QueryClient;
}): Promise<boolean> {
  if (isRecordingInProgress(opts).inProgress) {
    return true;
  }

  try {
    const [activeUpload, liveness] = await pTimeout(
      Promise.all([
        onlineChunkUploadBridge.getActive(),
        recorderFmc.get_recording_liveness(),
      ]),
      RECORDING_ACTIVE_PROBE_TIMEOUT_MS
    );
    const { isZombie } = classifyRecorderLiveness(liveness);
    return activeUpload.active || (!isZombie && liveness.state !== 'Stopped');
  } catch (error) {
    logger.warn('DESKTOP_RECORDING_ACTIVE_PROBE_FAILED', error);
    // Fail open (allow reload) on IPC failure. If IPC is wedged, the user needs
    // the reload to escape; failing safe (blocking reload) traps them.
    return false;
  }
}
